import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, ShoppingBag, Star, Loader2, X, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCartContext } from "@/contexts/CartContext";
import { useMyOrders, useMyReviews, useProducts, submitReview } from "@/lib/firestoreService";
import { findStatus } from "@/lib/seedData";
import { formatPrice } from "@/lib/cart";
import { useLang } from "@/lib/i18n";
import { useSEO } from "@/lib/useSEO";
import { track } from "@/lib/analytics";
import { orderNumber } from "@/lib/orderNumber";

function statusBadge(key) {
  if (key === "cancelled")
    return "bg-red-50 text-red-700 border-red-200";
  if (key === "delivered")
    return "bg-[#556B2F]/15 text-[#556B2F] border-[#556B2F]/30";
  if (key === "dispatched")
    return "bg-blue-50 text-blue-700 border-blue-200";
  if (["under_preparation", "quality_check", "ready_dispatch"].includes(key))
    return "bg-orange-50 text-orange-700 border-orange-200";
  if (["order_confirmed", "payment_confirmed"].includes(key))
    return "bg-[#5A2D1D]/10 text-[#5A2D1D] border-[#5A2D1D]/25";
  return "bg-yellow-50 text-yellow-700 border-yellow-200";
}

function fmtDate(seconds) {
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
}

const ORDERS_PAGE_SIZE = 10;

function SortHeader({ label, active, dir, onClick, className }) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1 hover:text-[#5A2D1D] ${className || ""}`}>
      {label} <Icon className="w-3 h-3" />
    </button>
  );
}

export const MyOrdersPage = ({ onAuthOpen }) => {
  useSEO({ title: "My Orders", description: "View and track your Annapurna Home Pickles order history." });
  const { t, isTe } = useLang();
  const { user, profile, firebaseReady } = useAuth();
  const orders = useMyOrders(user?.id);
  const myReviews = useMyReviews(user?.id);
  const products = useProducts();
  const cart = useCartContext();
  const [reviewOrder, setReviewOrder] = useState(null);
  const navigate = useNavigate();

  // "Buy Again" — re-add a past order's items (skipping anything no longer
  // available), then jump straight to checkout.
  const buyAgain = (order) => {
    const available = [];
    const skipped = [];
    (order.items || []).forEach((it) => {
      const product = products?.find(p => p.id === it.product_id);
      if (product && product.in_stock !== false) available.push(it);
      else skipped.push(it.name);
    });
    if (available.length === 0) {
      toast.error("Sorry, none of these items are available right now.");
      return;
    }
    cart.addItems(available);
    if (skipped.length > 0) {
      toast.warning(`${skipped.join(", ")} ${skipped.length > 1 ? "are" : "is"} no longer available and ${skipped.length > 1 ? "were" : "was"} left out.`);
    }
    track("buy_again_used", {
      source_order_id: order.id,
      item_count: available.length,
      skipped_count: skipped.length,
    });
    toast.success("Added to cart!");
    navigate("/checkout");
  };

  // sortBy: "date" (default, newest first) | "total"
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const toggleSort = (col) => {
    setPage(1);
    if (sortBy !== col) { setSortBy(col); setSortDir("desc"); return; }
    setSortDir((d) => (d === "desc" ? "asc" : "desc"));
  };

  const sortedOrders = orders ? [...orders].sort((a, b) => {
    let av, bv;
    if (sortBy === "total") { av = a.total || 0; bv = b.total || 0; }
    else { av = a.createdAt?.seconds || 0; bv = b.createdAt?.seconds || 0; }
    return sortDir === "asc" ? av - bv : bv - av;
  }) : [];

  const totalPages = Math.max(1, Math.ceil(sortedOrders.length / ORDERS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageOrders = sortedOrders.slice((safePage - 1) * ORDERS_PAGE_SIZE, safePage * ORDERS_PAGE_SIZE);

  if (!firebaseReady) {
    return (
      <Centered>
        <Package className="w-12 h-12 mx-auto mb-3 text-[#5A2D1D]/40" />
        <div className="font-display text-xl text-[#5A2D1D] mb-2">Order history unavailable</div>
        <p className="text-sm text-[#2E1A12]/70">Firebase is not connected. Contact us on WhatsApp to check your order.</p>
      </Centered>
    );
  }

  if (!user) {
    return (
      <Centered>
        <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-[#5A2D1D]/60" />
        <div className="font-display text-2xl text-[#5A2D1D] mb-2">Sign in to view your orders</div>
        <p className="text-sm text-[#2E1A12]/70 mb-5">Track your pickles from preparation to doorstep.</p>
        <button
          data-testid="orders-signin-button"
          onClick={onAuthOpen}
          className="bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-full font-semibold transition-colors"
        >
          {t.nav.login}
        </button>
      </Centered>
    );
  }

  return (
    <section data-testid="my-orders-page" className="py-6 sm:py-10 bg-[#F5E6CC] min-h-screen">
      <div className="max-w-4xl lg:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="mb-4 flex items-baseline justify-between gap-3 flex-wrap">
          <h1 className="font-display text-xl sm:text-2xl font-bold text-[#5A2D1D]">
            {t.orders.my_orders}
          </h1>
          {orders && orders.length > 0 && (
            <p className="text-xs text-[#2E1A12]/60">{orders.length} {t.orders.orders_placed}</p>
          )}
        </div>

        {orders === null ? (
          <div className="border border-[#5A2D1D]/20 rounded-sm overflow-hidden">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 px-5 py-3 bg-[#EFD9B4]/60 border-b border-[#5A2D1D]/15">
              {[80, 60, 48, 56, 40].map((w, i) => (
                <div key={i} className={`h-3 bg-[#5A2D1D]/15 rounded animate-pulse ${i >= 3 ? "hidden sm:block" : ""}`} style={{ width: `${w}%` }} />
              ))}
            </div>
            {[1, 2, 3].map(n => (
              <div key={n} className="grid grid-cols-3 sm:grid-cols-5 gap-4 px-5 py-4 border-b border-[#5A2D1D]/10">
                {[70, 50, 40, 60, 30].map((w, i) => (
                  <div key={i} className={`h-3 bg-[#5A2D1D]/10 rounded animate-pulse ${i >= 3 ? "hidden sm:block" : ""}`} style={{ width: `${w}%` }} />
                ))}
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="border border-[#5A2D1D]/20 rounded-sm p-12 text-center" data-testid="orders-empty">
            <Package className="w-14 h-14 mx-auto mb-4 text-[#5A2D1D]/30" />
            <div className="font-display text-xl text-[#5A2D1D] mb-2">{t.orders.empty}</div>
            <p className="text-sm text-[#2E1A12]/60 mb-6">{t.orders.first_order}</p>
            <Link
              to="/products"
              className="inline-block bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-6 py-3 rounded-full font-semibold transition-colors"
            >
              {t.orders.browse}
            </Link>
          </div>
        ) : (
          <>
            {/* Sort controls — shared by both mobile and desktop views */}
            <div className="flex items-center gap-4 mb-3 text-xs font-bold text-[#556B2F] uppercase tracking-widest">
              <span className="text-[#2E1A12]/40">Sort:</span>
              <SortHeader label="Date" active={sortBy === "date"} dir={sortDir} onClick={() => toggleSort("date")} />
              <SortHeader label="Total" active={sortBy === "total"} dir={sortDir} onClick={() => toggleSort("total")} />
            </div>

            {/* Mobile: stacked cards — avoids horizontal scrolling */}
            <div className="sm:hidden space-y-3" data-testid="orders-list-mobile">
              {pageOrders.map((o) => {
                const st = findStatus(o.status);
                const label = isTe ? st?.label_te : st?.label;
                const itemCount = o.items?.length || 0;
                const itemNames = (o.items || []).slice(0, 2).map(i => i.name).filter(Boolean);
                const extraItems = itemCount - 2;

                return (
                  <div
                    key={o.id}
                    data-testid={`order-row-${o.id}`}
                    onClick={() => navigate(`/order/${o.id}`)}
                    className="border border-[#5A2D1D]/20 rounded-sm p-4 bg-white/40 active:bg-[#EFD9B4]/40 cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-display font-bold text-[#5A2D1D]">
                        {orderNumber(o)}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full border ${statusBadge(o.status)}`}>
                        <span>{st?.icon}</span> {label}
                      </span>
                    </div>
                    <div className="text-xs text-[#2E1A12]/60 mt-1">{fmtDate(o.createdAt?.seconds)}</div>
                    <div className="text-sm text-[#2E1A12] font-medium mt-1.5">
                      {itemNames.join(", ")}
                      {extraItems > 0 && <span className="text-[#2E1A12]/50"> +{extraItems} more</span>}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-display font-bold text-[#5A2D1D]">{formatPrice(o.total || 0)}</span>
                      <div className="flex items-center gap-2">
                        {o.status === "delivered" && (
                          myReviews[o.id] ? (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#556B2F]">
                              <Star className="w-3.5 h-3.5 fill-[#556B2F]" /> Reviewed
                            </span>
                          ) : (
                            <button
                              data-testid={`review-button-${o.id}`}
                              onClick={(e) => { e.stopPropagation(); setReviewOrder(o); }}
                              className="inline-flex items-center gap-1 text-xs font-bold text-[#5A2D1D] border border-[#5A2D1D]/30 px-2.5 py-1 rounded-full"
                            >
                              <Star className="w-3.5 h-3.5" /> Review
                            </button>
                          )
                        )}
                        <button
                          data-testid={`buy-again-${o.id}`}
                          onClick={(e) => { e.stopPropagation(); buyAgain(o); }}
                          className="inline-flex items-center gap-1 text-xs font-bold text-[#556B2F] border border-[#556B2F]/40 px-2.5 py-1 rounded-full"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Buy Again
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop/tablet: full table, no horizontal scroll needed */}
            <div className="hidden sm:block border border-[#5A2D1D]/20 rounded-sm overflow-x-auto" data-testid="orders-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#EFD9B4]/70 border-b border-[#5A2D1D]/20">
                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-[#556B2F] whitespace-nowrap">Order</th>
                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-widest font-bold text-[#556B2F] whitespace-nowrap">
                      <SortHeader label="Date" active={sortBy === "date"} dir={sortDir} onClick={() => toggleSort("date")} />
                    </th>
                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">Items</th>
                    <th className="text-left px-3 py-3 text-[10px] uppercase tracking-widest font-bold text-[#556B2F] whitespace-nowrap">Status</th>
                    <th className="text-right px-3 py-3 text-[10px] uppercase tracking-widest font-bold text-[#556B2F] whitespace-nowrap">
                      <SortHeader label="Total" active={sortBy === "total"} dir={sortDir} onClick={() => toggleSort("total")} className="justify-end" />
                    </th>
                    <th className="text-center px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-[#556B2F] whitespace-nowrap">Review</th>
                    <th className="text-center px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-[#556B2F] whitespace-nowrap">Reorder</th>
                  </tr>
                </thead>
                <tbody>
                  {pageOrders.map((o, idx) => {
                    const st = findStatus(o.status);
                    const label = isTe ? st?.label_te : st?.label;
                    const itemCount = o.items?.length || 0;
                    const itemNames = (o.items || []).slice(0, 2).map(i => i.name).filter(Boolean);
                    const extraItems = itemCount - 2;

                    return (
                      <tr
                        key={o.id}
                        data-testid={`order-row-${o.id}`}
                        onClick={() => navigate(`/order/${o.id}`)}
                        className={`border-b border-[#5A2D1D]/10 hover:bg-[#EFD9B4]/30 transition-colors cursor-pointer ${idx % 2 === 0 ? "" : "bg-[#F5E6CC]/60"}`}
                      >
                        {/* Order ID */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Link
                            to={`/order/${o.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-display font-bold text-[#5A2D1D] hover:underline"
                          >
                            {orderNumber(o)}
                          </Link>
                        </td>

                        {/* Date */}
                        <td className="px-3 py-3 text-[#2E1A12]/60 whitespace-nowrap">
                          {fmtDate(o.createdAt?.seconds)}
                        </td>

                        {/* Items */}
                        <td className="px-3 py-3 max-w-[160px]">
                          <div className="text-[#2E1A12] font-medium truncate">
                            {itemNames.join(", ")}
                            {extraItems > 0 && <span className="text-[#2E1A12]/50"> +{extraItems}</span>}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-3">
                          <span
                            title={label}
                            className={`inline-flex items-center gap-1 max-w-[150px] overflow-hidden text-xs font-bold px-2 py-1 rounded-full border ${statusBadge(o.status)}`}
                          >
                            <span className="flex-shrink-0">{st?.icon}</span>
                            <span className="truncate">{label}</span>
                          </span>
                        </td>

                        {/* Total */}
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          <span className="font-display font-bold text-[#5A2D1D]">
                            {formatPrice(o.total || 0)}
                          </span>
                        </td>

                        {/* Review */}
                        <td className="px-4 py-3 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {o.status === "delivered" ? (
                            myReviews[o.id] ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-[#556B2F]">
                                <Star className="w-3.5 h-3.5 fill-[#556B2F]" /> Reviewed
                              </span>
                            ) : (
                              <button
                                data-testid={`review-button-${o.id}`}
                                onClick={() => setReviewOrder(o)}
                                className="inline-flex items-center gap-1 text-xs font-bold text-[#5A2D1D] border border-[#5A2D1D]/30 hover:bg-[#5A2D1D]/5 px-2.5 py-1 rounded-full transition-colors"
                              >
                                <Star className="w-3.5 h-3.5" /> Review
                              </button>
                            )
                          ) : (
                            <span className="text-[#2E1A12]/30">—</span>
                          )}
                        </td>

                        {/* Reorder */}
                        <td className="px-4 py-3 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            data-testid={`buy-again-${o.id}`}
                            onClick={() => buyAgain(o)}
                            className="inline-flex items-center gap-1 text-xs font-bold text-[#556B2F] border border-[#556B2F]/40 hover:bg-[#556B2F]/10 px-2.5 py-1 rounded-full transition-colors"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Buy Again
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-[#2E1A12]/50">Page {safePage} of {totalPages}</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="w-8 h-8 grid place-items-center rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] disabled:opacity-30 hover:bg-[#5A2D1D]/5"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="w-8 h-8 grid place-items-center rounded-sm border border-[#5A2D1D]/30 text-[#5A2D1D] disabled:opacity-30 hover:bg-[#5A2D1D]/5"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {reviewOrder && (
        <ReviewModal
          order={reviewOrder}
          user={user}
          profile={profile}
          onClose={() => setReviewOrder(null)}
        />
      )}
    </section>
  );
};

export const ReviewModal = ({ order, user, profile, onClose }) => {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);

  // De-duped list of distinct products in this order — a review attaches to
  // exactly one, so the customer picks which when there's more than one.
  const products = [];
  (order.items || []).forEach((it) => {
    if (!products.find(p => p.product_id === it.product_id)) {
      products.push({ product_id: it.product_id, name: it.name });
    }
  });
  const [productId, setProductId] = useState(products[0]?.product_id || null);

  const submit = async () => {
    setSaving(true);
    try {
      const chosen = products.find(p => p.product_id === productId);
      await submitReview({
        orderId: order.id,
        uid: user.id,
        customerName: profile?.name || user.email,
        rating,
        feedback,
        productId: chosen?.product_id,
        productName: chosen?.name,
      });
      track("review_submitted", {
        order_id: order.id,
        rating,
        product_id: chosen?.product_id,
        product_name: chosen?.name,
        has_feedback_text: !!feedback.trim(),
        feedback_length: feedback.trim().length,
      });
      toast.success("Thanks for your feedback!");
      onClose();
    } catch (e) {
      toast.error("Could not submit your review. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#2E1A12]/50 backdrop-blur-sm">
      <div data-testid="review-modal" className="w-full max-w-sm bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold text-[#5A2D1D]">Rate your order</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#5A2D1D]/10 rounded-sm text-[#5A2D1D]/50 hover:text-[#5A2D1D] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {products.length > 1 && (
          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-1.5">
              Which product is this review for?
            </label>
            <select
              data-testid="review-product-select"
              value={productId || ""}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-3 py-2 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
            >
              {products.map(p => (
                <option key={p.product_id} value={p.product_id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex justify-center gap-1.5 mb-4" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`review-star-${n}`}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHoverRating(n)}
              onMouseLeave={() => setHoverRating(0)}
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
            >
              <Star
                className={`w-8 h-8 transition-colors ${
                  n <= (hoverRating || rating) ? "fill-[#556B2F] text-[#556B2F]" : "text-[#5A2D1D]/25"
                }`}
              />
            </button>
          ))}
        </div>

        <textarea
          data-testid="review-feedback-input"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value.slice(0, 500))}
          maxLength={500}
          rows={4}
          placeholder="Tell us about your experience (optional)"
          className="w-full px-4 py-3 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-[#2E1A12] placeholder:text-[#2E1A12]/50 resize-none"
        />
        <div className="text-right text-[10px] text-[#2E1A12]/40 mt-1">{feedback.length}/500</div>

        <button
          data-testid="review-submit-button"
          onClick={submit}
          disabled={saving}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] py-3 rounded-full font-semibold transition-colors disabled:opacity-60"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Submit Review
        </button>
      </div>
    </div>
  );
};

const Centered = ({ children }) => (
  <div className="min-h-[60vh] grid place-items-center px-4">
    <div className="bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm p-10 max-w-md w-full text-center">
      {children}
    </div>
  </div>
);
